/** Selector do cabeçalho fixo do site de vendas. */
export const FIXED_NAV_SELECTOR = '.mc-nav';

const NAV_HEIGHT_FALLBACK_PX = 72;
const SCROLL_GUTTER_PX = 12;

/** Altura do menu fixo (px). Lê do DOM ou do CSS `--mc-nav-height`. */
export function getFixedNavOffsetPx(doc: Document = document): number {
  const nav = doc.querySelector<HTMLElement>(FIXED_NAV_SELECTOR);
  if (nav) {
    const h = nav.getBoundingClientRect().height;
    if (h > 0) return h;
  }

  const raw = getComputedStyle(doc.documentElement).getPropertyValue('--mc-nav-height').trim();
  if (raw) {
    const probe = doc.createElement('div');
    probe.style.position = 'absolute';
    probe.style.visibility = 'hidden';
    probe.style.height = raw;
    doc.body.appendChild(probe);
    const px = probe.getBoundingClientRect().height;
    probe.remove();
    if (px > 0) return px;
  }

  return NAV_HEIGHT_FALLBACK_PX;
}

/**
 * O `id` da secção fica no `<section>` com padding vertical grande.
 * Para o menu, queremos alinhar o título (h2) logo abaixo do cabeçalho.
 */
export function resolveSectionScrollTarget(section: HTMLElement): HTMLElement {
  return (
    section.querySelector<HTMLElement>(
      ':scope > div h2, :scope h2, :scope > div > .mc-reveal h2',
    ) ?? section
  );
}

/** Rola até `el` ficar imediatamente abaixo do menu fixo. */
export function scrollElementBelowNav(
  el: HTMLElement,
  behavior: ScrollBehavior = 'smooth',
  doc: Document = document,
): void {
  const win = doc.defaultView;
  if (!win) return;

  const scrollOnce = (scrollBehavior: ScrollBehavior): void => {
    const offset = getFixedNavOffsetPx(doc) + SCROLL_GUTTER_PX;
    const top = el.getBoundingClientRect().top + win.scrollY - offset;
    win.scrollTo({ top: Math.max(0, top), behavior: scrollBehavior });
  };

  scrollOnce(behavior);

  const fineTune = (): void => {
    const nav = doc.querySelector<HTMLElement>(FIXED_NAV_SELECTOR);
    if (!nav) return;
    const gap = el.getBoundingClientRect().top - nav.getBoundingClientRect().bottom;
    const delta = gap - SCROLL_GUTTER_PX;
    if (Math.abs(delta) > 0.5) {
      win.scrollBy({ top: delta, behavior: 'auto' });
    }
  };

  win.requestAnimationFrame(() => win.requestAnimationFrame(fineTune));
  if (behavior === 'smooth') {
    win.setTimeout(fineTune, 420);
    win.setTimeout(fineTune, 900);
  }
}

/** Secção por hash + alvo de scroll (título quando existir). */
export function anchorScrollTarget(
  hash: string,
  doc: Document = document,
): { section: HTMLElement; scrollTarget: HTMLElement } | null {
  if (!hash || hash === '#') return null;
  const id = hash.startsWith('#') ? hash.slice(1) : hash;
  const section = doc.getElementById(id);
  if (!section) return null;
  return { section, scrollTarget: resolveSectionScrollTarget(section) };
}
